<template>
  <!-- Tap a photo: see it big, with its caption and "3 of 24"; arrows, swipe
       and Esc work; focus goes to Close and comes back afterwards. -->
  <Teleport to="body">
    <div v-if="open" class="s2-lb" role="dialog" aria-modal="true" :aria-label="`Photo ${index + 1} of ${items.length}`" @keydown="onKey" @click.self="close">
      <button ref="closeBtn" type="button" class="s2-lb__close" aria-label="Close" @click="close">✕</button>
      <button v-if="items.length > 1" type="button" class="s2-lb__nav s2-lb__prev" aria-label="Previous photo" @click="go(-1)">‹</button>
      <figure class="s2-lb__fig" @touchstart.passive="touchStart" @touchend.passive="touchEnd">
        <img :src="current.src" v-bind="imgAttrs(current.src, '100vw')" :alt="current.caption || ''" class="s2-lb__img" />
        <figcaption class="s2-lb__cap"><span>{{ current.caption }}</span><span v-if="items.length > 1">{{ index + 1 }} of {{ items.length }}</span></figcaption>
      </figure>
      <button v-if="items.length > 1" type="button" class="s2-lb__nav s2-lb__next" aria-label="Next photo" @click="go(1)">›</button>
    </div>
  </Teleport>
</template>

<script setup>
import {imgAttrs} from "@/utils/photoSizes.js";
import {computed, nextTick, ref, watch} from "vue";

const props = defineProps({items: {type: Array, default: () => []}});
const open = ref(false);
const index = ref(0);
const closeBtn = ref(null);
let returnTo = null;
let startX = 0;
const current = computed(() => props.items[index.value] || {src: "", caption: ""});

function show(i) {
  if (!props.items.length) return;
  returnTo = typeof document !== "undefined" ? document.activeElement : null;
  index.value = Math.max(0, Math.min(props.items.length - 1, i));
  open.value = true;
}
function close() {
  open.value = false;
  nextTick(() => returnTo?.focus?.());
}
function go(d) {
  index.value = (index.value + d + props.items.length) % props.items.length;
}
function onKey(e) {
  if (e.key === "Escape") close();
  else if (e.key === "ArrowRight") go(1);
  else if (e.key === "ArrowLeft") go(-1);
}
function touchStart(e) { startX = e.touches[0].clientX; }
function touchEnd(e) {
  const dx = e.changedTouches[0].clientX - startX;
  if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
}
watch(open, (o) => { if (o) nextTick(() => closeBtn.value?.focus()); });
defineExpose({show});
</script>

<style scoped>
.s2-lb { position: fixed; inset: 0; z-index: 80; background: rgba(10, 12, 16, .92); display: flex; align-items: center; justify-content: center; padding: 3rem 1rem; }
.s2-lb__fig { margin: 0; max-width: min(1100px, 100%); max-height: 100%; display: flex; flex-direction: column; }
.s2-lb__img { max-width: 100%; max-height: calc(100vh - 9rem); object-fit: contain; display: block; margin: 0 auto; }
.s2-lb__cap { display: flex; justify-content: space-between; gap: 1rem; color: #e6e6e6; font: .9rem/1.4 var(--t-font-body, sans-serif); margin-top: .75rem; }
.s2-lb__close, .s2-lb__nav { position: absolute; background: rgba(255,255,255,.12); color: #fff; border: 0; border-radius: 999px; width: 44px; height: 44px; font-size: 1.4rem; cursor: pointer; }
.s2-lb__close { top: 1rem; right: 1rem; }
.s2-lb__prev { left: 1rem; top: 50%; transform: translateY(-50%); }
.s2-lb__next { right: 1rem; top: 50%; transform: translateY(-50%); }
.s2-lb button:focus-visible { outline: 3px solid #fff; outline-offset: 2px; }
</style>
